import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  limit as firestoreLimit,
  startAfter,
  getDocs,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Ticket, TicketMessage, TicketStatus, TicketType, TicketChannel } from '@/types';

// Hook to get user's tickets
export function useUserTickets(userId: string | undefined) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setTickets([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'tickets'),
      where('ownerId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ticketsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Ticket[];
        setTickets(ticketsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching tickets:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return { tickets, loading, error };
}

// Hook to get all tickets (admin) with pagination
const PAGE_SIZE = 25;

export function useAllTickets(filters?: {
  status?: TicketStatus;
  type?: TicketType;
  channel?: TicketChannel;
  search?: string;
}) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const buildConstraints = useCallback(() => {
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
    if (filters?.status) constraints.unshift(where('status', '==', filters.status));
    if (filters?.type) constraints.unshift(where('type', '==', filters.type));
    if (filters?.channel) constraints.unshift(where('channel', '==', filters.channel));
    return constraints;
  }, [filters?.status, filters?.type, filters?.channel]);

  const applySearch = useCallback((ticketsData: Ticket[]) => {
    if (!filters?.search) return ticketsData;
    const searchLower = filters.search.toLowerCase();
    return ticketsData.filter(
      (ticket) =>
        ticket.title.toLowerCase().includes(searchLower) ||
        ticket.description.toLowerCase().includes(searchLower) ||
        ticket.ownerEmail.toLowerCase().includes(searchLower)
    );
  }, [filters?.search]);

  // Initial load and filter changes
  useEffect(() => {
    setTickets([]);
    setLastDoc(null);
    setHasMore(true);
    setLoading(true);

    const constraints = buildConstraints();
    const q = query(collection(db, 'tickets'), ...constraints, firestoreLimit(PAGE_SIZE));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ticketsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Ticket[];

        setTickets(applySearch(ticketsData));
        setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMore(snapshot.docs.length === PAGE_SIZE);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching tickets:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [filters?.status, filters?.type, filters?.channel, filters?.search, buildConstraints, applySearch]);

  const loadMore = useCallback(async () => {
    if (!lastDoc || !hasMore || loadingMore) return;
    setLoadingMore(true);

    try {
      const constraints = buildConstraints();
      const q = query(
        collection(db, 'tickets'),
        ...constraints,
        startAfter(lastDoc),
        firestoreLimit(PAGE_SIZE)
      );

      const snapshot = await getDocs(q);
      const newTickets = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Ticket[];

      setTickets((prev) => [...prev, ...applySearch(newTickets)]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more tickets:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [lastDoc, hasMore, loadingMore, buildConstraints, applySearch]);

  return { tickets, loading, loadingMore, error, hasMore, loadMore };
}

// Hook to get a single ticket
export function useTicket(ticketId: string | undefined) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ticketId) {
      setTicket(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'tickets', ticketId),
      (snapshot) => {
        if (snapshot.exists()) {
          setTicket({ id: snapshot.id, ...snapshot.data() } as Ticket);
        } else {
          setTicket(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching ticket:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [ticketId]);

  return { ticket, loading, error };
}

// Hook to get ticket messages
export function useTicketMessages(ticketId: string | undefined) {
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ticketId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'tickets', ticketId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messagesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ticketId,
          ...doc.data(),
        })) as TicketMessage[];
        setMessages(messagesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching messages:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [ticketId]);

  return { messages, loading, error };
}


